<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import tiktokAdsAPI from 'dashboard/api/tiktokAds';

export default {
  components: { NextButton },
  data() {
    return {
      advertisers: [],
      audiences: [],
      loading: false,
      creating: false,
      form: { advertiserId: '', name: '', sourceType: 'all', sourceId: '' },
    };
  },
  computed: {
    ...mapGetters({
      segments: 'segments/getSegments',
      labels: 'labels/getLabels',
    }),
    sourceOptions() {
      if (this.form.sourceType === 'segment') return this.segments;
      if (this.form.sourceType === 'label') return this.labels;
      return [];
    },
    canCreate() {
      return (
        this.form.advertiserId &&
        this.form.name.trim() &&
        (this.form.sourceType === 'all' || this.form.sourceId)
      );
    },
  },
  mounted() {
    this.$store.dispatch('segments/get');
    this.$store.dispatch('labels/get');
    this.fetchAdvertisers();
    this.fetchAudiences();
  },
  methods: {
    async fetchAdvertisers() {
      try {
        const { data } = await tiktokAdsAPI.accountAdvertisers();
        this.advertisers = data || [];
        if (this.advertisers.length) {
          this.form.advertiserId = this.advertisers[0].id;
        }
      } catch (error) {
        this.advertisers = [];
      }
    },
    async fetchAudiences() {
      this.loading = true;
      try {
        const { data } = await tiktokAdsAPI.getAudiences();
        this.audiences = data || [];
      } catch (error) {
        this.audiences = [];
      } finally {
        this.loading = false;
      }
    },
    sourceLabel(audience) {
      if (audience.source_type === 'all') return 'Todos os contatos';
      return `${audience.source_type}: ${audience.source_id}`;
    },
    statusLabel(status) {
      const labels = {
        active: 'Sincronizado',
        error: 'Erro',
        pending: 'Sincronizando…',
      };
      return labels[status] || labels.pending;
    },
    async create() {
      if (!this.canCreate) return;
      this.creating = true;
      try {
        await tiktokAdsAPI.createAudience({
          advertiser_id: this.form.advertiserId,
          name: this.form.name,
          source_type: this.form.sourceType,
          source_id: this.form.sourceType === 'all' ? '' : this.form.sourceId,
        });
        this.form = {
          advertiserId: this.form.advertiserId,
          name: '',
          sourceType: 'all',
          sourceId: '',
        };
        useAlert('Público criado — sincronizando.');
        this.fetchAudiences();
      } catch (error) {
        useAlert('Não foi possível criar o público.');
      } finally {
        this.creating = false;
      }
    },
    async resync(id) {
      await tiktokAdsAPI.syncAudience(id);
      useAlert('Re-sincronizando.');
      this.fetchAudiences();
    },
    async remove(id) {
      await tiktokAdsAPI.deleteAudience(id);
      this.fetchAudiences();
    },
  },
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">Públicos</h1>
      <p class="mt-1 text-sm text-n-slate-10">
        Suba sua base do CRM (segmento, label ou todos) como Custom Audience do
        TikTok (email/telefone hasheado) para remarketing e lookalike — igual ao
        Meta e Google.
      </p>
    </div>

    <div
      v-if="!advertisers.length"
      class="p-4 text-sm rounded-lg text-n-amber-11 bg-n-amber-3"
    >
      Conecte o TikTok primeiro na aba Conexões.
    </div>

    <template v-else>
      <div
        class="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 rounded-xl border border-n-weak bg-n-alpha-black2"
      >
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          Conta de anúncio
          <select
            v-model="form.advertiserId"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option v-for="a in advertisers" :key="a.id" :value="a.id">
              {{ a.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          Nome do público
          <input
            v-model="form.name"
            type="text"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            placeholder="Ex: Base CRM - Clientes"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          Origem
          <select
            v-model="form.sourceType"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option value="all">Todos os contatos</option>
            <option value="segment">Segmento</option>
            <option value="label">Label</option>
          </select>
        </label>
        <label
          v-if="form.sourceType !== 'all'"
          class="flex flex-col gap-1 text-xs text-n-slate-10"
        >
          {{ form.sourceType === 'segment' ? 'Segmento' : 'Label' }}
          <select
            v-model="form.sourceId"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option value="">Selecionar…</option>
            <option
              v-for="opt in sourceOptions"
              :key="opt.id"
              :value="form.sourceType === 'label' ? opt.title : opt.id"
            >
              {{ opt.name || opt.title }}
            </option>
          </select>
        </label>
        <div class="md:col-span-2 flex justify-end">
          <NextButton
            label="Criar e sincronizar"
            sm
            :is-loading="creating"
            :disabled="!canCreate"
            @click="create"
          />
        </div>
      </div>

      <div
        v-if="!audiences.length"
        class="p-4 text-sm text-center rounded-lg text-n-slate-11 bg-n-alpha-black2"
      >
        Nenhum público criado ainda.
      </div>

      <div
        v-for="audience in audiences"
        :key="audience.id"
        class="flex items-center justify-between gap-3 p-4 rounded-lg bg-n-alpha-black2"
      >
        <div class="min-w-0">
          <p class="font-medium truncate text-n-slate-12">
            {{ audience.name }}
          </p>
          <p class="text-xs text-n-slate-10">
            {{ sourceLabel(audience) }} · {{ audience.synced_count }} contatos ·
            {{ statusLabel(audience.status) }}
          </p>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <NextButton
            icon="i-lucide-refresh-cw"
            faded
            xs
            @click="resync(audience.id)"
          />
          <NextButton
            icon="i-lucide-trash-2"
            ruby
            faded
            xs
            @click="remove(audience.id)"
          />
        </div>
      </div>
    </template>
  </div>
</template>
